"use client";

import { CheckCircle2, FileSignature, ShieldCheck, Upload, XCircle } from "lucide-react";
import { makeFunctionReference } from "convex/server";
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  buildCarrierOnboardingProgress,
  buildVerifyCarrierAgentDraft,
  mockFmcsaOnboardingLookup,
  type CarrierOnboardingState,
} from "@/core/portal/carrier-onboarding";

const startOrUpdate = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    token: string;
    carrierMc: string;
    carrierName?: string;
    dotNumber?: string;
    coiDocumentId?: string;
    w9DocumentId?: string;
    signedAgreementName?: string;
  },
  { sessionId: string; token: string; fmcsaSnapshot: Record<string, unknown> }
>("carrierOnboarding:startOrUpdate");
const submit = makeFunctionReference<
  "mutation",
  { tenantId: string; token: string; finalPayload?: Record<string, unknown> },
  { status: "submitted" | "blocked"; actionId?: string; blockedReasons?: string[] }
>("carrierOnboarding:submit");

export function CarrierOnboardingPortal({ tenantId, token }: { tenantId: string; token?: string }) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <CarrierOnboardingContent tenantId={tenantId} initialToken={token} />;
  }
  if (!token) {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <div className="rounded-md border border-border bg-card p-5">
          <h1 className="text-lg font-semibold">Secure onboarding link required</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Ask the broker to send a current carrier onboarding link.
          </p>
        </div>
      </main>
    );
  }
  return <CarrierOnboardingLive tenantId={tenantId} token={token} />;
}

function CarrierOnboardingLive({ tenantId, token }: { tenantId: string; token: string }) {
  const save = useMutation(startOrUpdate);
  const submitMutation = useMutation(submit);
  return (
    <CarrierOnboardingContent
      tenantId={tenantId}
      initialToken={token}
      onSave={(state, currentToken) =>
        save({
          tenantId,
          token: currentToken,
          carrierMc: state.carrierMc ?? "",
          carrierName: state.carrierName,
          dotNumber: state.dotNumber,
          coiDocumentId: state.coiDocumentId,
          w9DocumentId: state.w9DocumentId,
          signedAgreementName: state.signedAgreementName,
        })
      }
      onSubmit={(currentToken) => submitMutation({ tenantId, token: currentToken, finalPayload: { source: "carrier_self_onboarding" } })}
    />
  );
}

function CarrierOnboardingContent({
  tenantId,
  onSave,
  onSubmit,
  initialToken,
}: {
  tenantId: string;
  initialToken?: string;
  onSave?: (state: CarrierOnboardingState, token: string) => Promise<{ token: string }>;
  onSubmit?: (token: string) => Promise<{ status: string; blockedReasons?: string[] }>;
}) {
  const [token, setToken] = useState<string | undefined>(initialToken);
  const [state, setState] = useState<CarrierOnboardingState>({ tenantId });
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const progress = useMemo(() => buildCarrierOnboardingProgress(state), [state]);
  const draft = useMemo(() => buildVerifyCarrierAgentDraft(state), [state]);

  async function runFmcsa() {
    if (!state.carrierMc) return;
    const fmcsaSnapshot = mockFmcsaOnboardingLookup({
      tenantId,
      carrierMc: state.carrierMc,
      carrierName: state.carrierName,
    });
    const next = { ...state, carrierName: state.carrierName ?? fmcsaSnapshot.carrierName, fmcsaSnapshot };
    setState(next);
    const currentToken = token ?? `demo_${state.carrierMc}`;
    const result = await onSave?.(next, currentToken);
    if (result?.token) setToken(result.token);
  }

  async function finish() {
    if (!progress.complete) {
      setMessage(progress.blockedReasons.join("; "));
      return;
    }
    const currentToken = token ?? `demo_${state.carrierMc}`;
    await onSave?.(state, currentToken);
    const result = await onSubmit?.(currentToken);
    if (result?.status === "blocked") {
      setMessage(result.blockedReasons?.join("; ") ?? "Onboarding blocked");
      return;
    }
    setSubmitted(true);
    setMessage("Submitted to Haulbase Agent for carrier review.");
  }

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Carrier Onboarding</h1>
          <p className="text-sm text-muted-foreground">MC verification, COI, W9, and broker agreement intake.</p>
        </div>
        <Badge variant={submitted ? "good" : progress.complete ? "info" : "warn"}>
          {submitted ? "submitted" : progress.complete ? "ready" : "incomplete"}
        </Badge>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Carrier Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <input
                value={state.carrierMc ?? ""}
                onChange={(event) => setState((current) => ({ ...current, carrierMc: event.target.value }))}
                placeholder="MC number"
                className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
              />
              <input
                value={state.carrierName ?? ""}
                onChange={(event) => setState((current) => ({ ...current, carrierName: event.target.value }))}
                placeholder="Legal carrier name"
                className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
              />
            </div>
            <Button variant="secondary" onClick={runFmcsa} disabled={!state.carrierMc}>
              <ShieldCheck size={16} />
              Check FMCSA
            </Button>
            {state.fmcsaSnapshot ? (
              <div className="rounded-md border border-border p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span>Mock FMCSA authority</span>
                  <Badge variant={state.fmcsaSnapshot.blocked ? "bad" : "good"}>
                    {state.fmcsaSnapshot.authorityStatus}
                  </Badge>
                </div>
                <div className="mt-2 text-muted-foreground">
                  Insurance {state.fmcsaSnapshot.insuranceStatus}; safety {state.fmcsaSnapshot.safetyRating}; risk{" "}
                  {state.fmcsaSnapshot.riskScore}
                </div>
              </div>
            ) : null}

            <DocumentInput
              label="Certificate of insurance"
              value={state.coiDocumentId}
              onChange={(value) => setState((current) => ({ ...current, coiDocumentId: value }))}
            />
            <DocumentInput
              label="W9"
              value={state.w9DocumentId}
              onChange={(value) => setState((current) => ({ ...current, w9DocumentId: value }))}
            />
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Broker agreement signature</span>
              <input
                value={state.signedAgreementName ?? ""}
                onChange={(event) => setState((current) => ({ ...current, signedAgreementName: event.target.value }))}
                placeholder="Signer full name"
                className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm outline-none"
              />
            </label>
            <Button onClick={finish} disabled={submitted}>
              <FileSignature size={16} />
              Submit for review
            </Button>
            {message ? <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">{message}</div> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {progress.steps.map((step) => (
              <div key={step.id} className="flex items-center gap-2 text-sm">
                {step.complete ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-amber-600" />}
                {step.label}
              </div>
            ))}
            <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              Agent draft ready: {draft.ready ? "yes" : "no"}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function DocumentInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <span className="grid grid-cols-[1fr_auto] gap-2">
        <input
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={`${label} document id`}
          className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
        />
        <span className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-3 text-sm text-muted-foreground">
          <Upload size={16} />
          Upload
        </span>
      </span>
    </label>
  );
}
